import { twoPlans, threePlans, fourPlans, frenchPlans } from './options.js'

// All 11 logical steps (used for STEP_FIELDS completion tracking)
export const STEPS = [
  { id: 1,  title: 'Student'    },
  { id: 2,  title: 'School'     },
  { id: 3,  title: 'Skills'     },
  { id: 4,  title: 'Language'   },
  { id: 5,  title: 'French'     },
  { id: 6,  title: 'Math'       },
  { id: 7,  title: 'Science'    },
  { id: 8,  title: 'Social St.' },
  { id: 9,  title: 'Health/PE'  },
  { id: 10, title: 'Arts'       },
  { id: 11, title: 'ERS'        },
]

// Subject step IDs - steps 4–9 are owned by the "Subjects" parent node
export const SUBJECT_STEP_IDS = new Set([4, 5, 6, 7, 8, 9])

// Top-level indicator nodes (6 nodes - Subjects is a parent for 4–9)
export const TOP_STEPS = [
  { id: 1,          title: 'Student'  },
  { id: 2,          title: 'School'   },
  { id: 3,          title: 'Skills'   },
  { id: 'subjects', title: 'Subjects', group: { stepIds: [4, 5, 6, 7, 8, 9], jumpTo: 4 } },
  { id: 10,         title: 'Arts'     },
  { id: 11,         title: 'ERS'      },
]

// Sub-row nodes - always visible beneath the top row
export const SUB_STEPS = [
  { id: 4, title: 'Language' },
  { id: 5, title: 'French'   },
  { id: 6, title: 'Math'     },
  { id: 7, title: 'Science'  },
  { id: 8, title: 'Social'   },
  { id: 9, title: 'H&PE'     },
]

export const STEP_FIELDS = {
  1:  ['studentName', 'oen', 'date', 'grade', 'teacher'],
  2:  ['board', 'school', 'daysAbsent', 'totalDaysAbsent', 'timesLate', 'totalTimesLate'],
  3:  ['responsibility1','organization1','independentWork1','collaboration1','initiative1','selfRegulation1',
       'responsibility2','organization2','independentWork2','collaboration2','initiative2','selfRegulation2',
       'strengthsNextStepsLSWH'],
  4:  ['languagePlan', 'languageGrade1', 'languageGrade2', 'languageComments'],
  5:  ['frenchPlan', 'frenchListeningGrade1', 'frenchListeningGrade2'],
  6:  ['mathematicsPlan', 'mathematicsGrade1', 'mathematicsGrade2', 'mathematicsComments'],
  7:  ['scienceAndTechnologyPlan', 'scienceAndTechnologyGrade1', 'scienceAndTechnologyGrade2'],
  8:  ['socialStudiesPlan', 'socialStudiesGrade1', 'socialStudiesGrade2'],
  9:  ['healthEducationGrade1', 'healthEducationGrade2', 'physicalEducationGrade1', 'physicalEducationGrade2'],
  10: ['artsHeader', 'arts1Grade1', 'arts1Grade2'],
  11: ['ersCompleted', 'ersBenchmark'],
}

export const FIELD_LABELS = {
  date: 'Date',
  studentName: 'Student Name',
  oen: 'OEN',
  grade: 'Grade',
  gradeInSeptember: 'Grade in September',
  teacher: 'Teacher',
  board: 'School Board',
  school: 'School',
  address1: 'Address Line 1',
  address2: 'Address Line 2',
  principal: 'Principal',
  telephone: 'Telephone',
  daysAbsent: 'Days Absent',
  totalDaysAbsent: 'Total Days Absent',
  timesLate: 'Times Late',
  totalTimesLate: 'Total Times Late',
  responsibility1: 'Responsibility (Term 1)',
  organization1: 'Organization (Term 1)',
  independentWork1: 'Independent Work (Term 1)',
  collaboration1: 'Collaboration (Term 1)',
  initiative1: 'Initiative (Term 1)',
  selfRegulation1: 'Self-Regulation (Term 1)',
  responsibility2: 'Responsibility (Term 2)',
  organization2: 'Organization (Term 2)',
  independentWork2: 'Independent Work (Term 2)',
  collaboration2: 'Collaboration (Term 2)',
  initiative2: 'Initiative (Term 2)',
  selfRegulation2: 'Self-Regulation (Term 2)',
  strengthsNextStepsLSWH: 'Strengths & Next Steps (Learning Skills)',
  languagePlan: 'Language - Plan',
  languageGrade1: 'Language - Grade (Term 1)',
  languageGrade2: 'Language - Grade (Term 2)',
  languageComments: 'Language - Comments',
  nativeLanguage: 'Native Language Name',
  nativeLanguagePlan: 'Native Language - Plan',
  nativeLanguageGrade1: 'Native Language - Grade (Term 1)',
  nativeLanguageGrade2: 'Native Language - Grade (Term 2)',
  nativeLanguageComments: 'Native Language - Comments',
  frenchPlan: 'French - Plan',
  frenchListeningPlan: 'French Listening - Plan',
  frenchListeningGrade1: 'French Listening - Grade (Term 1)',
  frenchListeningGrade2: 'French Listening - Grade (Term 2)',
  frenchSpeakingPlan: 'French Speaking - Plan',
  frenchSpeakingGrade1: 'French Speaking - Grade (Term 1)',
  frenchSpeakingGrade2: 'French Speaking - Grade (Term 2)',
  frenchReadingPlan: 'French Reading - Plan',
  frenchReadingGrade1: 'French Reading - Grade (Term 1)',
  frenchReadingGrade2: 'French Reading - Grade (Term 2)',
  frenchWritingPlan: 'French Writing - Plan',
  frenchWritingGrade1: 'French Writing - Grade (Term 1)',
  frenchWritingGrade2: 'French Writing - Grade (Term 2)',
  frenchComments: 'French - Comments',
  mathematicsPlan: 'Mathematics - Plan',
  mathematicsGrade1: 'Mathematics - Grade (Term 1)',
  mathematicsGrade2: 'Mathematics - Grade (Term 2)',
  mathematicsComments: 'Mathematics - Comments',
  scienceAndTechnologyPlan: 'Science & Technology - Plan',
  scienceAndTechnologyGrade1: 'Science & Technology - Grade (Term 1)',
  scienceAndTechnologyGrade2: 'Science & Technology - Grade (Term 2)',
  scienceAndTechnologyComments: 'Science & Technology - Comments',
  socialStudiesPlan: 'Social Studies - Plan',
  socialStudiesGrade1: 'Social Studies - Grade (Term 1)',
  socialStudiesGrade2: 'Social Studies - Grade (Term 2)',
  socialStudiesComments: 'Social Studies - Comments',
  healthEducationPlan: 'Health Education - Plan',
  healthEducationGrade1: 'Health Education - Grade (Term 1)',
  healthEducationGrade2: 'Health Education - Grade (Term 2)',
  physicalEducationPlan: 'Physical Education - Plan',
  physicalEducationGrade1: 'Physical Education - Grade (Term 1)',
  physicalEducationGrade2: 'Physical Education - Grade (Term 2)',
  healthAndPhysicalEducationComments: 'Health & PE - Comments',
  artsHeader: 'Arts Header',
  arts1: 'Arts 1 Name',
  arts1Plan: 'Arts 1 - Plan',
  arts1Grade1: 'Arts 1 - Grade (Term 1)',
  arts1Grade2: 'Arts 1 - Grade (Term 2)',
  arts2: 'Arts 2 Name',
  arts2Plan: 'Arts 2 - Plan',
  arts2Grade1: 'Arts 2 - Grade (Term 1)',
  arts2Grade2: 'Arts 2 - Grade (Term 2)',
  arts3: 'Arts 3 Name',
  arts3Plan: 'Arts 3 - Plan',
  arts3Grade1: 'Arts 3 - Grade (Term 1)',
  arts3Grade2: 'Arts 3 - Grade (Term 2)',
  arts4: 'Arts 4 Name',
  arts4Plan: 'Arts 4 - Plan',
  arts4Grade1: 'Arts 4 - Grade (Term 1)',
  arts4Grade2: 'Arts 4 - Grade (Term 2)',
  artsComments: 'Arts - Comments',
  optionalStudies: 'Optional Studies Name',
  optionalStudiesPlan: 'Optional Studies - Plan',
  optionalStudiesGrade1: 'Optional Studies - Grade (Term 1)',
  optionalStudiesGrade2: 'Optional Studies - Grade (Term 2)',
  optionalStudiesComments: 'Optional Studies - Comments',
  ersCompleted: 'ERS Completed',
  ersDateYear: 'ERS Date - Year',
  ersDateMonth: 'ERS Date - Month',
  ersDateDay: 'ERS Date - Day',
  ersBenchmark: 'ERS Benchmark',
}

// Subject card configs used by the renderStep function in Page.jsx
export const SUBJECT_CARDS = [
  { id: 4,  title: 'Language',             planField: 'languagePlan',             grade1Field: 'languageGrade1',             grade2Field: 'languageGrade2',             commentsField: 'languageComments',             plans: twoPlans,   nameField: null },
  { id: 4,  title: 'Native Language',      planField: 'nativeLanguagePlan',       grade1Field: 'nativeLanguageGrade1',       grade2Field: 'nativeLanguageGrade2',       commentsField: 'nativeLanguageComments',       plans: twoPlans,   nameField: 'nativeLanguage' },
  { id: 5,  title: 'French',               planField: 'frenchPlan',               grade1Field: null,                         grade2Field: null,                         commentsField: 'frenchComments',               plans: frenchPlans, nameField: null },
  { id: 6,  title: 'Mathematics',          planField: 'mathematicsPlan',          grade1Field: 'mathematicsGrade1',          grade2Field: 'mathematicsGrade2',          commentsField: 'mathematicsComments',          plans: threePlans, nameField: null },
  { id: 7,  title: 'Science & Technology', planField: 'scienceAndTechnologyPlan', grade1Field: 'scienceAndTechnologyGrade1', grade2Field: 'scienceAndTechnologyGrade2', commentsField: 'scienceAndTechnologyComments', plans: threePlans, nameField: null },
  { id: 8,  title: 'Social Studies',       planField: 'socialStudiesPlan',        grade1Field: 'socialStudiesGrade1',        grade2Field: 'socialStudiesGrade2',        commentsField: 'socialStudiesComments',        plans: threePlans, nameField: null },
  { id: 9,  title: 'Health Education',     planField: 'healthEducationPlan',      grade1Field: 'healthEducationGrade1',      grade2Field: 'healthEducationGrade2',      commentsField: null,                           plans: threePlans, nameField: null },
  { id: 9,  title: 'Physical Education',   planField: 'physicalEducationPlan',    grade1Field: 'physicalEducationGrade1',    grade2Field: 'physicalEducationGrade2',    commentsField: null,                           plans: threePlans, nameField: null },
]

export const ARTS_ROWS = [
  { nameField: 'arts1', planField: 'arts1Plan', grade1Field: 'arts1Grade1', grade2Field: 'arts1Grade2', placeholder: 'e.g. Visual Arts' },
  { nameField: 'arts2', planField: 'arts2Plan', grade1Field: 'arts2Grade1', grade2Field: 'arts2Grade2', placeholder: 'e.g. Music' },
  { nameField: 'arts3', planField: 'arts3Plan', grade1Field: 'arts3Grade1', grade2Field: 'arts3Grade2', placeholder: 'e.g. Drama' },
  { nameField: 'arts4', planField: 'arts4Plan', grade1Field: 'arts4Grade1', grade2Field: 'arts4Grade2', placeholder: 'e.g. Dance' },
]
